/**
 * Central Dutch (nl-NL) copy for the entire app. Keep all user-facing
 * strings here so tone and terminology stay consistent across screens.
 */
export const nl = {
  common: {
    appName: 'Fotoboek Studio',
    save: 'Opslaan',
    cancel: 'Annuleren',
    close: 'Sluiten',
    delete: 'Verwijderen',
    duplicate: 'Dupliceren',
    rename: 'Naam wijzigen',
    back: 'Terug',
    next: 'Volgende',
    previous: 'Vorige',
    continue: 'Doorgaan',
    confirm: 'Bevestigen',
    loading: 'Laden…',
    retry: 'Opnieuw proberen',
    search: 'Zoeken',
    yes: 'Ja',
    no: 'Nee',
    optional: '(optioneel)',
    unknownError: 'Er is een onverwachte fout opgetreden.',
    minutesAgoOne: '1 minuut geleden',
    minutesAgo: '{{count}} minuten geleden',
    justNow: 'Net nu',
    today: 'Vandaag',
  },
  nav: {
    projects: 'Mijn projecten',
    account: 'Account',
    login: 'Inloggen',
    logout: 'Uitloggen',
    guest: 'Gast',
    privacy: 'Privacy',
  },
  intro: {
    title: 'Maak je eigen fotoboek',
    subtitle:
      'Ontwerp offline of online een fotoboek met je mooiste herinneringen. Alles wordt lokaal opgeslagen op je apparaat.',
    newProject: 'Nieuw fotoboek',
    openProject: 'Project openen',
    loadDemo: 'Demoproject laden',
    continueAsGuest: 'Werken als gast — je project blijft op dit apparaat bewaard.',
    loginCta: 'Al een account? Inloggen',
  },
  auth: {
    login: {
      title: 'Inloggen',
      subtitle: 'Log in om je projecten te synchroniseren tussen apparaten.',
      email: 'E-mailadres',
      password: 'Wachtwoord',
      submit: 'Inloggen',
      forgotPassword: 'Wachtwoord vergeten?',
      noAccount: 'Nog geen account?',
      registerCta: 'Registreer je hier',
      offlineNotice:
        'Geen verbinding met de server. Je kunt offline verdergaan als gast; je projecten blijven lokaal bewaard.',
      continueAsGuest: 'Verder als gast',
      success: 'Je bent ingelogd.',
    },
    register: {
      title: 'Account aanmaken',
      subtitle: 'Bewaar je fotoboeken veilig in de cloud en werk op meerdere apparaten.',
      name: 'Naam',
      email: 'E-mailadres',
      password: 'Wachtwoord',
      passwordHint: 'Minimaal 8 tekens.',
      submit: 'Account aanmaken',
      haveAccount: 'Heb je al een account?',
      loginCta: 'Log hier in',
    },
    forgotPassword: {
      title: 'Wachtwoord vergeten',
      subtitle: 'Vul je e-mailadres in en we sturen je een herstellink.',
      email: 'E-mailadres',
      submit: 'Herstellink versturen',
      sent: 'Als dit e-mailadres bekend is, ontvang je een herstellink.',
      backToLogin: 'Terug naar inloggen',
    },
    resetPassword: {
      title: 'Nieuw wachtwoord instellen',
      subtitle: 'Kies een nieuw wachtwoord voor je account.',
      password: 'Nieuw wachtwoord',
      confirmPassword: 'Bevestig wachtwoord',
      submit: 'Wachtwoord opslaan',
      mismatch: 'De wachtwoorden komen niet overeen.',
      success: 'Je wachtwoord is gewijzigd. Je kunt nu inloggen.',
      invalidToken: 'Deze herstellink is ongeldig of verlopen.',
    },
    errors: {
      invalidCredentials: 'E-mailadres of wachtwoord is onjuist.',
      emailInUse: 'Dit e-mailadres is al in gebruik.',
      network: 'Kan geen verbinding maken met de server. Probeer het later opnieuw.',
      weakPassword: 'Kies een wachtwoord van minimaal 8 tekens.',
      generic: 'Er ging iets mis. Probeer het opnieuw.',
    },
  },
  privacy: {
    title: 'Privacy',
    intro:
      'Fotoboek Studio bewaart je projecten en foto’s standaard lokaal op je eigen apparaat, in de opslag van je browser.',
    body: [
      'Wanneer je als gast werkt, verlaat niets je apparaat: projecten, foto’s en instellingen staan lokaal in de browseropslag (IndexedDB / bestandssysteemopslag).',
      'Als je inlogt met een account kun je ervoor kiezen projecten naar de cloud te synchroniseren zodat je op meerdere apparaten kunt werken. Dit gebeurt alleen na jouw uitdrukkelijke keuze per project.',
      'Je kunt op elk moment een lokale backup (.fotoboek) maken en terugzetten, zodat je nooit afhankelijk bent van één apparaat of dienst.',
      'Voor vragen over je gegevens kun je contact opnemen via het account­scherm.',
    ],
  },
  projects: {
    title: 'Mijn projecten',
    localSection: 'Op dit apparaat',
    cloudSection: 'In de cloud',
    empty: {
      title: 'Nog geen fotoboeken',
      body: 'Begin met je eerste fotoboek. Je werk wordt automatisch lokaal opgeslagen.',
      cta: 'Nieuw fotoboek maken',
    },
    open: 'Openen',
    lastEdited: 'Laatst bewerkt',
    pages: '{{count}} pagina’s',
    importBackup: 'Backup importeren',
    exportBackup: 'Backup exporteren',
    deleteConfirmTitle: 'Project verwijderen?',
    deleteConfirmBody:
      'Dit verwijdert “{{title}}” van dit apparaat. Deze actie kan niet ongedaan worden gemaakt.',
    newProject: 'Nieuw fotoboek',
    syncPending: 'Synchronisatie bezig…',
    localOnly: 'Alleen lokaal',
    cloud: {
      saveToCloud: 'Opslaan in de cloud',
      saveSuccess: '“{{title}}” is opgeslagen in de cloud.',
      empty: 'Nog geen projecten in de cloud.',
      opening: 'Project wordt geopend…',
    },
  },
  wizard: {
    title: 'Nieuw fotoboek',
    steps: {
      product: 'Product',
      details: 'Details',
      template: 'Startpunt',
      photos: "Foto's",
      location: 'Opslag',
    },
    product: {
      heading: 'Kies je fotoboek',
      pageCount: 'Aantal pagina’s',
      pageCountHint: 'Tussen {{min}} en {{max}}, in stappen van {{step}}.',
    },
    details: {
      heading: 'Geef je fotoboek een naam',
      titleLabel: 'Titel',
      titlePlaceholder: 'Bijv. Zomervakantie 2026',
      descriptionLabel: 'Beschrijving',
      descriptionPlaceholder: 'Optionele beschrijving voor jezelf',
      titleRequired: 'Vul een titel in om door te gaan.',
    },
    template: {
      heading: 'Kies een startpunt (optioneel)',
      blank: 'Beginnen met lege pagina’s',
      skip: 'Later kiezen',
    },
    photos: {
      heading: 'Foto’s toevoegen (optioneel)',
      body: 'Je kunt nu al foto’s importeren, of dit later in de editor doen.',
      addPhotos: 'Foto’s toevoegen',
      selectedCount: '{{count}} foto’s geselecteerd',
      skip: 'Overslaan',
    },
    location: {
      heading: 'Waar wil je dit project opslaan?',
      local: 'Lokaal op dit apparaat',
      localHint: 'Snel en werkt altijd offline. Maak zelf backups.',
      cloud: 'In de cloud',
      cloudHint: 'Vereist een account. Synchroniseert tussen apparaten.',
      loginRequired: 'Log in of registreer om in de cloud op te slaan.',
    },
    create: 'Fotoboek aanmaken',
    creating: 'Fotoboek wordt aangemaakt…',
  },
  editor: {
    cropDone: 'Gereed',
    topbar: {
      back: 'Terug naar projecten',
      untitled: 'Naamloos fotoboek',
      savedAt: 'Opgeslagen om {{time}}',
      saved: 'Opgeslagen',
      saving: 'Opslaan…',
      unsaved: 'Niet-opgeslagen wijzigingen',
      saveError: 'Opslaan mislukt — wordt opnieuw geprobeerd',
      undo: 'Ongedaan maken',
      redo: 'Opnieuw uitvoeren',
      zoomIn: 'Inzoomen',
      zoomOut: 'Uitzoomen',
      zoomFit: 'Passend maken',
      preview: 'Voorvertoning',
      export: 'Exporteren',
      guest: 'Gastmodus',
      account: 'Account',
      snapToggle: 'Magnetisch uitlijnen',
      cloudSyncing: 'Cloud synchroniseren…',
      cloudSynced: 'Gesynchroniseerd',
      cloudOffline: 'Offline — wordt later gesynchroniseerd',
      cloudError: 'Synchroniseren mislukt',
    },
    tabs: {
      photos: "Foto's",
      templates: 'Templates',
      text: 'Tekst',
      backgrounds: 'Achtergronden',
      shapes: 'Vormen',
    },
    photos: {
      importButton: "Foto's toevoegen",
      dropHint: 'Sleep foto’s hierheen of klik om te bladeren',
      searchPlaceholder: 'Zoeken in foto’s',
      sortRecent: 'Meest recent',
      sortName: 'Naam',
      sortUnused: 'Ongebruikt eerst',
      filterUsed: 'Alleen gebruikt',
      filterUnused: 'Alleen ongebruikt',
      empty: 'Nog geen foto’s geïmporteerd.',
      used: 'Gebruikt',
      unused: 'Ongebruikt',
      deleteConfirmTitle: 'Foto verwijderen?',
      deleteConfirmBodyUsed:
        'Deze foto wordt gebruikt op {{count}} plek(ken) in je fotoboek. Verwijderen maakt die kaders leeg.',
      deleteConfirmBodyUnused: 'Deze foto wordt nergens gebruikt.',
      importing: '{{count}} foto’s worden geïmporteerd…',
    },
    templates: {
      empty: 'Geen templates gevonden.',
      applyToPage: 'Toepassen op huidige pagina',
      categories: {
        foto: "Foto's",
        grid: 'Grids',
        tekst: 'Tekst',
        leeg: 'Leeg',
        cover: 'Kaft',
      },
      redistribute: 'Bestaande foto’s herverdelen',
    },
    text: {
      addHeading: 'Titel toevoegen',
      addBody: 'Tekstblok toevoegen',
      addCaption: 'Onderschrift toevoegen',
    },
    backgrounds: {
      heading: 'Achtergrondkleur',
      swatches: 'Kleuren',
      custom: 'Aangepaste kleur',
      none: 'Geen',
    },
    shapes: {
      rectangle: 'Rechthoek',
      ellipse: 'Ovaal',
      line: 'Lijn',
    },
    properties: {
      empty: 'Selecteer een element om eigenschappen te bewerken.',
      photo: {
        heading: 'Foto',
        replace: 'Vervangen',
        remove: 'Uit kader verwijderen',
        zoom: 'Zoom',
        rotation: 'Rotatie',
        flipH: 'Spiegelen horizontaal',
        flipV: 'Spiegelen verticaal',
        cornerRadius: 'Hoekafronding',
        filters: 'Filters',
        brightness: 'Helderheid',
        contrast: 'Contrast',
        saturation: 'Verzadiging',
        warmth: 'Warmte',
        grayscale: 'Zwart-wit',
        dpiOk: 'Resolutie is goed voor afdrukken',
        dpiWarn: 'Resolutie is aan de lage kant',
        dpiLow: 'Resolutie is laag — afdrukresultaat kan wazig zijn',
      },
      text: {
        heading: 'Tekst',
        content: 'Inhoud',
        font: 'Lettertype',
        size: 'Grootte',
        weight: 'Gewicht',
        color: 'Kleur',
        align: 'Uitlijning',
        lineHeight: 'Regelhoogte',
        letterSpacing: 'Letterafstand',
      },
      shape: {
        heading: 'Vorm',
        fill: 'Vulkleur',
        stroke: 'Lijnkleur',
        strokeWidth: 'Lijndikte',
        cornerRadius: 'Hoekafronding',
      },
      common: {
        position: 'Positie',
        size: 'Grootte',
        rotation: 'Rotatie',
        opacity: 'Doorzichtigheid',
        layerForward: 'Naar voren',
        layerBackward: 'Naar achteren',
        lock: 'Vergrendelen',
        unlock: 'Ontgrendelen',
      },
    },
    pageStrip: {
      cover: 'Kaft',
      spread: 'Spread {{number}}',
      addPage: 'Pagina toevoegen',
      removePage: 'Pagina verwijderen',
      duplicatePage: 'Pagina dupliceren',
      removeConfirmTitle: 'Pagina verwijderen?',
      removeConfirmBody: 'De inhoud van deze pagina gaat verloren.',
    },
    shortcuts: {
      title: 'Toetsenbord­sneltoetsen',
      undo: 'Ongedaan maken',
      redo: 'Opnieuw uitvoeren',
      delete: 'Verwijderen',
      duplicate: 'Dupliceren',
      escape: 'Selectie opheffen / bewerkmodus verlaten',
      enter: 'Bewerkmodus bevestigen',
      save: 'Direct opslaan',
      zoomIn: 'Inzoomen',
      zoomOut: 'Uitzoomen',
      nextPage: 'Volgende spread',
      prevPage: 'Vorige spread',
    },
  },
  preview: {
    title: 'Voorvertoning',
    back: 'Terug naar editor',
    cover: 'Kaft',
    pageOf: 'Pagina {{current}} van {{total}}',
  },
  export: {
    title: 'Exporteren',
    subtitle: 'Controleer eventuele problemen voordat je exporteert.',
    noIssues: 'Geen problemen gevonden. Je fotoboek is klaar om te exporteren.',
    issuesFound: '{{count}} punt(en) om te controleren',
    severities: {
      blocking: 'Blokkerend',
      error: 'Fout',
      warning: 'Waarschuwing',
      info: 'Info',
    },
    exportPdf: 'Exporteer als PDF',
    exporting: 'Bezig met exporteren…',
    cancel: 'Annuleren',
    cancelled: 'Export geannuleerd.',
    success: 'Export voltooid. Het bestand wordt gedownload.',
    failure: 'Exporteren is mislukt. Probeer het opnieuw.',
    goToIssue: 'Bekijk',
    allowLowDpi: 'Exporteren toestaan ondanks lage resolutie',
    progressLabel: 'Pagina {{current}} van {{total}}',
  },
  account: {
    title: 'Account',
    guestNotice: 'Je werkt nu als gast. Log in om te synchroniseren tussen apparaten.',
    loggedInAs: 'Ingelogd als {{email}}',
    logout: 'Uitloggen',
    storage: {
      heading: 'Opslag op dit apparaat',
      used: 'Gebruikt: {{used}} van {{quota}}',
      persisted: 'Duurzame opslag is actief — je gegevens blijven bewaard.',
      notPersisted: 'Duurzame opslag is niet gegarandeerd. Maak regelmatig een backup.',
      requestPersist: 'Duurzame opslag aanvragen',
    },
    backup: {
      heading: 'Backup',
      exportAll: 'Alle projecten exporteren',
      importFile: 'Backupbestand importeren',
    },
  },
  emptyStates: {
    noProjects: 'Je hebt nog geen fotoboeken.',
    noPhotos: 'Nog geen foto’s.',
    noTemplates: 'Geen templates beschikbaar.',
  },
  errors: {
    projectNotFound: 'Dit project kan niet worden gevonden op dit apparaat.',
    corruptProject: 'Dit projectbestand kan niet worden gelezen. Het is mogelijk beschadigd.',
    unsupportedSchema: 'Dit project is gemaakt met een nieuwere versie van de app.',
    storageQuota: 'Er is niet genoeg opslagruimte beschikbaar op dit apparaat.',
    importFailed: 'Importeren is mislukt.',
    backupInvalid: 'Dit backupbestand is ongeldig of beschadigd.',
  },
} as const;

export type Messages = typeof nl;
